import React, { useEffect, useState } from 'react';
import './Quality.css';
import isoCert from '../assets/certificates/iso-certificate.jpeg';
import iso2009Cert from '../assets/certificates/iso-2009-certificate.jpeg';
import iafCert from '../assets/certificates/iaf-certificate.jpeg';
import egacCert from '../assets/certificates/egac-certificate.jpeg';
import iclCert from '../assets/certificates/icl-certificate.jpeg';

const Quality = () => {
  const [activeCert, setActiveCert] = useState(null);

  useEffect(() => {
    if (!activeCert) return;
    const onKey = e => {
      if (e.key === 'Escape') setActiveCert(null);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [activeCert]);

  const certificates = [
    {
      id: 'iso-22000',
      image: isoCert,
      title: 'ISO 22000:2018',
      subtitle: 'Food Safety Management',
      issuer: 'International Standard'
    },
    {
      id: 'iso-9001',
      image: iso2009Cert,
      title: 'ISO 9001:2015',
      subtitle: 'Quality Management System',
      issuer: 'International Standard'
    },
    {
      id: 'iaf',
      image: iafCert,
      title: 'IAF Recognition',
      subtitle: 'International Accreditation Forum',
      issuer: 'Global Accreditation'
    },
    {
      id: 'egac',
      image: egacCert,
      title: 'EGAC Accredited',
      subtitle: 'Egyptian Accreditation Council',
      issuer: 'IAF MLA Signatory'
    },
    {
      id: 'icl',
      image: iclCert,
      title: 'ICL Certification',
      subtitle: 'Independent Certification Body',
      issuer: 'Compliance Verified'
    }
  ];

  const qualityFeatures = [
    {
      id: 1,
      title: 'BRC Certification',
      description: 'BRC Global Standard for Packaging and Packaging Materials certification ensures food safety and quality.',
      icon: 'fas fa-certificate',
      color: '#10b981'
    },
    {
      id: 2,
      title: 'ISO 22000 Certified',
      description: 'Food Safety Management System certification for consistent quality and safety standards.',
      icon: 'fas fa-shield-alt',
      color: '#3b82f6'
    },
    {
      id: 3,
      title: 'In-Process Quality Control',
      description: 'Comprehensive IPQC system monitoring every phase from raw material to final product.',
      icon: 'fas fa-clipboard-check',
      color: '#f59e0b'
    },
    {
      id: 4,
      title: 'Testing Laboratory',
      description: 'State-of-the-art testing facility for material analysis and quality verification.',
      icon: 'fas fa-microscope',
      color: '#8b5cf6'
    },
    {
      id: 5,
      title: 'International Standards',
      description: 'Compliance with international packaging standards and regulations.',
      icon: 'fas fa-globe',
      color: '#ef4444'
    },
    {
      id: 6,
      title: 'Continuous Improvement',
      description: 'Regular quality audits and continuous improvement processes.',
      icon: 'fas fa-chart-line',
      color: '#06b6d4'
    }
  ];

  return (
    <section id="quality" className="quality section">
      <div className="container">
        <div className="section-header text-center">
          <h2 className="section-title">Quality Assurance</h2>
          <p className="section-subtitle">
            Committed to excellence through rigorous quality control and international certifications
          </p>
        </div>

        <div className="quality-intro">
          <div className="quality-text">
            <h3>Our Quality Commitment</h3>
            <p>
              Our Company has its own well equipped in-house quality control department 
              carry out "In Process Quality Control" (IPQC) in each and every phase of 
              production, from raw material to final product.
            </p>
            <p>
              We maintain the highest standards of quality through continuous monitoring, 
              testing, and improvement processes. Our certifications ensure that every 
              product meets international standards and customer expectations.
            </p>
          </div>
          <div className="quality-image">
            <div className="image-placeholder">
              <i className="fas fa-award"></i>
              <p>Quality Excellence</p>
            </div>
          </div>
        </div>

        <div className="quality-features">
          <div className="features-grid">
            {qualityFeatures.map(feature => (
              <div key={feature.id} className="feature-card">
                <div className="feature-icon" style={{ backgroundColor: feature.color }}>
                  <i className={feature.icon}></i>
                </div>
                <div className="feature-content">
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="certifications">
          <div className="cert-header">
            <span className="cert-eyebrow">
              <i className="fas fa-medal"></i> Verified & Accredited
            </span>
            <h3>Our Certifications</h3>
            <p>Recognized by leading international certification bodies. Click any certificate to view the full document.</p>
          </div>
          <div className="cert-gallery">
            {certificates.map(cert => (
              <button
                key={cert.id}
                type="button"
                className="cert-card"
                onClick={() => setActiveCert(cert)}
                aria-label={`View ${cert.title} certificate`}
              >
                <span className="cert-ribbon">
                  <i className="fas fa-certificate"></i>
                </span>
                <div className="cert-thumb">
                  <img src={cert.image} alt={`${cert.title} certificate`} loading="lazy" />
                  <span className="cert-thumb-overlay">
                    <i className="fas fa-search-plus"></i>
                    <span>View Certificate</span>
                  </span>
                </div>
                <div className="cert-meta">
                  <h4>{cert.title}</h4>
                  <p>{cert.subtitle}</p>
                  <span className="cert-issuer">
                    <i className="fas fa-check-circle"></i> {cert.issuer}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {activeCert && (
          <div
            className="cert-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`${activeCert.title} certificate`}
            onClick={() => setActiveCert(null)}
          >
            <div className="cert-lightbox-inner" onClick={e => e.stopPropagation()}>
              <button
                type="button"
                className="cert-lightbox-close"
                onClick={() => setActiveCert(null)}
                aria-label="Close certificate view"
              >
                <i className="fas fa-times"></i>
              </button>
              <img src={activeCert.image} alt={`${activeCert.title} certificate`} />
              <div className="cert-lightbox-caption">
                <h4>{activeCert.title}</h4>
                <p>{activeCert.subtitle}</p>
              </div>
            </div>
          </div>
        )}

        <div className="quality-process">
          <h3>Quality Process</h3>
          <div className="process-steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Raw Material Testing</h4>
                <p>Comprehensive testing of incoming raw materials for quality and compliance</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>In-Process Monitoring</h4>
                <p>Continuous monitoring during manufacturing to ensure consistent quality</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Final Inspection</h4>
                <p>Thorough inspection of finished products before packaging and dispatch</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4>Documentation</h4>
                <p>Complete documentation and traceability for all quality processes</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Quality;
